import { z } from 'zod';
import { sameArticle } from './aisles';
import { prefHits, type FoodPrefs } from './meals';
import { QUICK_MINUTES, RECIPE_RESPONSE_SCHEMA, type Ingredient, type RecipeTag } from './recipes';

/**
 * "Was essen wir heute?" (Küche 4, 1.22): three suggestions for a day, without AI – at once and
 * offline. Mike, 14.10.: "möglichst immer Rezeptvorschläge für heute … nee, gefällt mir nicht,
 * mach andere Vorschläge. Vielleicht gibt es immer drei Vorschläge." The family's own recipes
 * come first, the Larovi-Ideenbuch fills in; allergies never show up, the day's calendar decides
 * how quick it should be.
 */

const DAY = 86_400_000;
/** Cooked or planned this recently – not suggested again. */
export const RECENT_DAYS = 7;
/** Per page: this many of the family's recipes, the rest from the Ideenbuch. */
const OWN_PER_PAGE = 2;
export const PER_PAGE = 3;

/** What a dish is mainly made of – for variety ("gestern schon Nudeln"). */
export type Base = 'pasta' | 'rice' | 'potato' | 'bread' | 'soup' | 'egg' | 'sweet' | 'other';

const BASES: [Base, RegExp][] = [
  ['soup', /(suppe|eintopf|soup|stew|chowder)/i],
  [
    'sweet',
    /(milchreis|kaiserschmarrn|pfannkuchen|arme ritter|waffel|grießbrei|pancake|rice pudding|french toast)/i,
  ],
  [
    'pasta',
    /(nudel|spaghetti|pasta|penne|lasagne|tortellini|gnocchi|spätzle|maultasche|noodle|macaroni)/i,
  ],
  ['rice', /(reis|risotto|rice|couscous|bulgur|quinoa)/i],
  ['potato', /(kartoffel|pommes|puffer|potato|chips|wedges)/i],
  ['bread', /(brot|wrap|pizza|flammkuchen|burger|toast|quesadilla|tortilla|bread|flatbread)/i],
  ['egg', /(rührei|spiegelei|omelett|shakshuka|senfeier|eggs?)\b/i],
];

/** Title first (it names the dish), then the ingredients. */
export function baseOf(title: string, ingredients: Pick<Ingredient, 'name'>[] = []): Base {
  for (const [b, re] of BASES) if (re.test(title)) return b;
  for (const [b, re] of BASES) if (ingredients.some((i) => re.test(i.name))) return b;
  return 'other';
}

/** A dish that could be suggested: one of the family's recipes or one from the Ideenbuch. */
export interface Candidate {
  /** "r:<recipeId>" or "i:<ideaId>" – stable across days. */
  key: string;
  kind: 'own' | 'idea';
  title: string;
  minutes: number | null;
  tags: RecipeTag[];
  ingredients: Pick<Ingredient, 'name'>[];
  lastCooked: number | null;
  cookCount: number;
  /** Kids' "Freu mich" in earlier plans with this dish. */
  likes: number;
}

/** What the family wants today (the buttons above the suggestions). */
export type MealWish = 'quick' | 'veggie' | 'kids';

export interface SuggestContext {
  candidates: Candidate[];
  members: { id: string; name: string; food?: FoodPrefs }[];
  /** Keys cooked or planned within the last days (and already in this week's plan). */
  recent: string[];
  /** What yesterday's meal was mainly made of – today something else first. */
  yesterday: Base | null;
  /** A full evening – quick dishes first. */
  busy: boolean;
  wishes: MealWish[];
  now: number;
  /** Changes the order a little from day to day, stays the same within a day ("YYYY-MM-DD"). */
  seed: string;
}

/** Why a card is shown – one short reason, the app words it. */
export type Reason =
  | { kind: 'kids' }
  | { kind: 'longAgo'; days: number }
  | { kind: 'quick'; minutes: number }
  | { kind: 'new' }
  | { kind: 'often' }
  | { kind: 'minutes'; minutes: number }
  | { kind: 'fits'; have: string[]; missing: string[] };

export interface Suggestion {
  candidate: Candidate;
  reason: Reason | null;
  /** Someone dislikes something in it ("Ben mag keine Pilze") – shown, not hidden. */
  dislikedBy: string[];
}

/** Small stable hash – the day's order is the same on every device of the family. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967295;
}

const isQuick = (c: Candidate) => c.minutes !== null && c.minutes <= QUICK_MINUTES;

/** Allowed at all today? Allergies and the wishes are hard rules, everything else is points. */
function allowed(c: Candidate, ctx: SuggestContext, recent: Set<string>): boolean {
  if (recent.has(c.key)) return false;
  if (prefHits(c.ingredients, ctx.members).some((h) => h.kind === 'allergy')) return false;
  if (ctx.wishes.includes('quick') && !isQuick(c)) return false;
  if (ctx.wishes.includes('veggie') && !c.tags.includes('veggie')) return false;
  if (ctx.wishes.includes('kids') && !c.tags.includes('kids') && c.likes === 0) return false;
  return true;
}

function score(c: Candidate, ctx: SuggestContext): number {
  let s = hash(`${ctx.seed}|${c.key}`) * 12;
  if (ctx.busy) {
    if (isQuick(c)) s += 30;
    else if ((c.minutes ?? 45) > 45) s -= 30;
  }
  if (c.kind === 'own') {
    // long not cooked climbs; never cooked is interesting too
    const days = c.lastCooked === null ? 20 : (ctx.now - c.lastCooked) / DAY;
    s += Math.min(days, 60) / 3;
    s += Math.min(c.cookCount, 10) * 1.5;
  }
  s += c.likes * 8;
  if (c.tags.includes('kids')) s += 4;
  s -= prefHits(c.ingredients, ctx.members).filter((h) => h.kind === 'dislike').length * 12;
  if (
    ctx.yesterday &&
    ctx.yesterday !== 'other' &&
    baseOf(c.title, c.ingredients) === ctx.yesterday
  )
    s -= 20;
  return s;
}

function reasonFor(c: Candidate, ctx: SuggestContext): Reason | null {
  if (c.likes > 0 || (c.kind === 'own' && c.tags.includes('kids'))) return { kind: 'kids' };
  if (c.kind === 'own' && c.lastCooked !== null) {
    const days = Math.floor((ctx.now - c.lastCooked) / DAY);
    if (days >= 10) return { kind: 'longAgo', days };
  }
  if (ctx.busy && isQuick(c)) return { kind: 'quick', minutes: c.minutes! };
  if (c.kind === 'idea') return { kind: 'new' };
  if (c.cookCount >= 3) return { kind: 'often' };
  return c.minutes !== null ? { kind: 'minutes', minutes: c.minutes } : null;
}

function toSuggestion(c: Candidate, ctx: SuggestContext, reason?: Reason): Suggestion {
  return {
    candidate: c,
    reason: reason ?? reasonFor(c, ctx),
    dislikedBy: [
      ...new Set(
        prefHits(c.ingredients, ctx.members)
          .filter((h) => h.kind === 'dislike')
          .map((h) => h.name),
      ),
    ],
  };
}

/** Every allowed dish, best first – own recipes and ideas ranked separately. */
export function rankMeals(ctx: SuggestContext): { own: Suggestion[]; ideas: Suggestion[] } {
  const recent = new Set(ctx.recent);
  const ranked = ctx.candidates
    .filter((c) => allowed(c, ctx, recent))
    .map((c) => ({ c, s: score(c, ctx) }))
    .sort((a, b) => b.s - a.s || a.c.title.localeCompare(b.c.title));
  return {
    own: ranked.filter((x) => x.c.kind === 'own').map((x) => toSuggestion(x.c, ctx)),
    ideas: ranked.filter((x) => x.c.kind === 'idea').map((x) => toSuggestion(x.c, ctx)),
  };
}

/** How many different pages "Andere Vorschläge" can show before it starts again. */
export function pageCount(r: { own: Suggestion[]; ideas: Suggestion[] }): number {
  const total = r.own.length + r.ideas.length;
  return Math.max(1, Math.ceil(total / PER_PAGE));
}

/**
 * One page of three: two of the family's own recipes and one idea – fewer own ones (a new
 * family) are filled from the Ideenbuch and the other way round. "Andere Vorschläge" goes to the
 * next page and after the last one starts again; no dish twice across the pages.
 */
export function suggestionPage(
  r: { own: Suggestion[]; ideas: Suggestion[] },
  page: number,
): Suggestion[] {
  const pages = pageCount(r);
  const p = ((page % pages) + pages) % pages;
  // lay out all pages once, then pick – every dish lands on exactly one page
  const own = [...r.own];
  const ideas = [...r.ideas];
  let out: Suggestion[] = [];
  for (let i = 0; i <= p; i++) {
    out = [];
    const wantOwn = Math.min(OWN_PER_PAGE, own.length);
    out.push(...own.splice(0, wantOwn));
    out.push(...ideas.splice(0, PER_PAGE - out.length));
    if (out.length < PER_PAGE) out.push(...own.splice(0, PER_PAGE - out.length));
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// "Was ist da?" – the fridge question (no AI)
// ---------------------------------------------------------------------------------------------

/** Always in the kitchen – never "fehlt". */
const STAPLES =
  /^(salz|pfeffer|öl|olivenöl|rapsöl|wasser|zucker|mehl|butter|essig|senf|brühe|gemüsebrühe|paprikapulver|oregano|salt|pepper|oil|olive oil|water|sugar|flour|vinegar|stock|black pepper)$/i;

/** "Paprika, Feta und 3 Kartoffeln" → ["Paprika", "Feta", "Kartoffeln"]. */
export function splitFridge(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[,;\n]|\s+und\s+|\s+and\s+/i)
        .map((s) => s.replace(/^\s*\d+[.,]?\d*\s*\w{0,3}\.?\s+/, '').trim())
        .filter((s) => s.length >= 2),
    ),
  ].slice(0, 20);
}

const usesItem = (ingredient: string, item: string) => {
  const i = ingredient.toLowerCase();
  const w = item.toLowerCase();
  return sameArticle(ingredient, item) || i.includes(w) || (w.length > 4 && w.includes(i));
};

/**
 * Dishes that use what is there – the more of it and the less missing, the better. Allergies
 * still never show up; the reason says what fits and what is missing.
 */
export function matchFridge(have: string[], ctx: SuggestContext): Suggestion[] {
  if (!have.length) return [];
  const recent = new Set(ctx.recent);
  const hits: { c: Candidate; used: string[]; missing: string[]; s: number }[] = [];
  for (const c of ctx.candidates) {
    if (!allowed(c, { ...ctx, wishes: ctx.wishes.filter((w) => w !== 'quick') }, recent)) continue;
    const used = have.filter((h) => c.ingredients.some((i) => usesItem(i.name, h)));
    if (!used.length) continue;
    const missing = c.ingredients
      .map((i) => i.name)
      .filter((n) => !STAPLES.test(n.trim()) && !have.some((h) => usesItem(n, h)));
    const s =
      used.length * 10 - missing.length * 2 + (c.kind === 'own' ? 3 : 0) + score(c, ctx) / 20;
    hits.push({ c, used, missing, s });
  }
  return hits
    .sort((a, b) => b.s - a.s)
    .map((h) => toSuggestion(h.c, ctx, { kind: 'fits', have: h.used, missing: h.missing }));
}

// ---------------------------------------------------------------------------------------------
// The day: how much time is there for cooking?
// ---------------------------------------------------------------------------------------------

export interface DayEvent {
  title: string;
  /** "HH:MM"; absent = all day (does not count). */
  start?: string;
  end?: string;
  /** Who is in it – names; empty or several for family events. */
  people: string[];
}

export type DayContext =
  | { kind: 'busyPerson'; name: string; until: string; title: string }
  | { kind: 'busy' }
  | { kind: 'relaxed' }
  | null;

const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/**
 * One sentence about the day: someone is out until shortly before dinner → quick ("Lotta hat bis
 * 18:15 Schwimmen"); lots on in the evening → quick; a free weekend evening → time for something
 * new. `weekday` 1 = Monday … 7 = Sunday.
 */
export function dayContext(events: DayEvent[], mealTime: string, weekday: number): DayContext {
  const meal = mins(mealTime);
  // out in the 90 minutes before dinner (or still out at dinner time)
  const late = events
    .filter((e) => e.start && e.end)
    .filter((e) => mins(e.end!) > meal - 90 && mins(e.start!) < meal + 30)
    .sort((a, b) => mins(b.end!) - mins(a.end!));
  if (late.length === 1 && late[0]!.people.length === 1) {
    const e = late[0]!;
    return { kind: 'busyPerson', name: e.people[0]!, until: e.end!, title: e.title };
  }
  if (late.length > 0) return { kind: 'busy' };
  const evening = events.filter((e) => e.start && mins(e.start) >= meal - 240);
  if (evening.length >= 3) return { kind: 'busy' };
  if (weekday >= 6 && evening.length === 0) return { kind: 'relaxed' };
  return null;
}

export const isBusy = (d: DayContext) => d?.kind === 'busy' || d?.kind === 'busyPerson';

// ---------------------------------------------------------------------------------------------
// "Überrasch uns" – three new ideas from the AI, only when the family asks for them
// ---------------------------------------------------------------------------------------------

export const MealIdeasInput = z.object({
  wishes: z
    .array(z.enum(['quick', 'veggie', 'kids']))
    .max(3)
    .default([]),
  /** "Was ist da?" – what is in the fridge. */
  have: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  /** The app's language (the AI answers in it). */
  locale: z.string().max(10).nullish(),
});
export type MealIdeasInput = z.infer<typeof MealIdeasInput>;

/** Three recipes in the same shape as a read recipe. */
export const MEAL_IDEAS_SCHEMA = {
  type: 'object',
  properties: {
    recipes: { type: 'array', items: RECIPE_RESPONSE_SCHEMA },
  },
  required: ['recipes'],
} as const;

export interface IdeasBrief {
  wishes: MealWish[];
  have: string[];
  /** Never in a dish (allergies of the family). */
  avoid: string[];
  /** Better without (dislikes). */
  dislike: string[];
  /** The family knows these already – something else, please. */
  known: string[];
}

/**
 * Instructions for new ideas – fully in the app's language (mixed languages made the model mix,
 * Mike 16.10.). The marker "[ideas]" lets the test fake answer. Only food words go to the AI:
 * no names, no ages, nothing about the family.
 */
export function mealIdeasInstructions(locale: 'de' | 'en', b: IdeasBrief): string {
  const list = (xs: string[]) => xs.slice(0, 30).join(', ');
  if (locale === 'en') {
    return [
      '[ideas] You suggest exactly three different family dinners that are easy to cook at home. Answer in English only.',
      'Each as a full recipe for 4 people: ingredients one per line with amount first ("400 g spaghetti"), steps one per entry, total minutes, a short kidsTip.',
      b.wishes.includes('quick') ? 'Each one ready in 30 minutes at most.' : '',
      b.wishes.includes('veggie') ? 'All vegetarian: no meat, no fish.' : '',
      b.wishes.includes('kids') ? 'Mild dishes children like.' : '',
      b.have.length
        ? `Use as many of these as sensible: ${list(b.have)}. Missing things may be added.`
        : '',
      b.avoid.length ? `Never use (allergies): ${list(b.avoid)}.` : '',
      b.dislike.length ? `Better without: ${list(b.dislike)}.` : '',
      b.known.length ? `Not these, the family knows them: ${list(b.known)}.` : '',
      'Leave unsure empty.',
    ]
      .filter(Boolean)
      .join('\n');
  }
  return [
    '[ideas] Du schlägst genau drei verschiedene Familien-Abendessen vor, die man zu Hause einfach kochen kann. Antworte nur auf Deutsch.',
    'Jedes als vollständiges Rezept für 4 Personen: Zutaten eine pro Zeile mit Menge vorne ("400 g Spaghetti"), Arbeitsschritte je ein Eintrag, Gesamtzeit in Minuten, ein kurzer kidsTip.',
    b.wishes.includes('quick') ? 'Jedes Gericht ist in höchstens 30 Minuten fertig.' : '',
    b.wishes.includes('veggie') ? 'Alles vegetarisch: kein Fleisch, kein Fisch.' : '',
    b.wishes.includes('kids') ? 'Milde Gerichte, die Kinder mögen.' : '',
    b.have.length
      ? `Verwende möglichst viel davon: ${list(b.have)}. Fehlendes darf dazukommen.`
      : '',
    b.avoid.length ? `Niemals verwenden (Allergien): ${list(b.avoid)}.` : '',
    b.dislike.length ? `Lieber ohne: ${list(b.dislike)}.` : '',
    b.known.length ? `Nicht diese, die kennt die Familie schon: ${list(b.known)}.` : '',
    'unsure leer lassen.',
  ]
    .filter(Boolean)
    .join('\n');
}
